import isEqual from 'lodash/isEqual';
import type { StoreActionApi } from 'react-sweet-state';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { TEAM_NAME } from '../../../../common/constants';
import { categoryGuidToString, type Category } from '../../../../common/types';
import { orderCategories as reorderCategoriesService } from '../../../../services/order-categories';
import type { State } from '../../types';

export const moveCategoryFromIndexToIndex = (
	categories: Category[],
	fromIndex: number,
	toIndex: number,
): Category[] => {
	const newCategories = [...categories];
	const movedItem = newCategories.splice(fromIndex, 1)[0];
	if (movedItem !== undefined) {
		newCategories.splice(toIndex, 0, movedItem);
	}
	return newCategories;
};

export const reorderCategories =
	(
		projectId: number,
		fromIndex: number,
		toIndex: number,
		analyticsEvent: UIAnalyticsEvent,
		activationId: string,
		onReorderMismatch: () => void,
		onReorderFailure: () => void,
	) =>
	async ({ setState, getState }: StoreActionApi<State>) => {
		const categoryToBeMovedId = getState().categoriesData.categories[fromIndex].id;
		const initiallyOrderedCategories = [...getState().categoriesData.categories];
		const newlyOrderedCategories = moveCategoryFromIndexToIndex(
			getState().categoriesData.categories,
			fromIndex,
			toIndex,
		);
		setState({
			...getState(),
			categoriesData: {
				...getState().categoriesData,
				categories: newlyOrderedCategories,
			},
		});

		try {
			const { categories } = await reorderCategoriesService({
				projectId,
				activationId,
				categories: [
					{
						categoryId: categoryToBeMovedId,
						order: toIndex,
					},
				],
			});

			fireTrackAnalytics(analyticsEvent, 'category reordered', {
				id: categoryGuidToString(categoryToBeMovedId),
			});

			const expectedCategoryIdOrder = newlyOrderedCategories.map((category) => category.id);
			const actualCategoryIdOrder = categories.map((categoryResult) => categoryResult.id);

			if (!isEqual(expectedCategoryIdOrder, actualCategoryIdOrder)) {
				// Something about the category information in apollo is different to our store
				// e.g. another user has reordered categories, added a category, deleted a category
				setState({
					...getState(),
					categoriesData: {
						...getState().categoriesData,
						categories,
					},
				});
				onReorderMismatch();
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					id: 'reorderCategories',
					packageName: 'jiraServicedeskKnowledgeCommon',
					teamName: TEAM_NAME,
				},
				error,
			});
			setState({
				...getState(),
				categoriesData: {
					...getState().categoriesData,
					categories: [...initiallyOrderedCategories],
				},
			});
			onReorderFailure();
		}
	};
